/**
 * 来訪履歴 `/reception/history`(設計書 §10.1、計画 第 7 段)。
 * 1 行 = 来訪者 1 人。期間(既定は直近 30 日)と拠点で絞り、CSV に書き出せる。
 * 見える範囲と PII の列の有無は API が決める(`pii_columns=false` なら氏名・会社・件名の列を出さない)。
 * 匿名化済みの行は押せない(来訪の詳細 API が 404 を返すため)。「匿名化済み」のタグを出す。
 */
import { DownloadOutlined } from "@ant-design/icons";
import { Alert, Button, DatePicker, Select, Space, Typography, message } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ApiError } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { DataTable, PageHeader, StatusTag, type DataColumn } from "../../ui";
import { errorText } from "./hooks";
import {
  ASSURANCE,
  CHECKIN_ROUTE,
  CORE,
  VISITOR_STATUS,
  VISIT_STATUS,
  fmtRange,
  useHistory,
  useMyLocations,
  type HistoryRow,
} from "./visits";

const { Text } = Typography;
const PAGE = 50;

const VERIFIED_METHOD: Record<string, string> = {
  code_later: "後から 6 桁コード",
  operator_in_person: "受付で確認",
  host_in_person: "担当者が確認",
};

/** CSV を落とす(Authorization が要るので blob にする。hr/loanHooks の useDownload と同じ作り)。 */
function useCsvDownload() {
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const run = useCallback(
    async (path: string) => {
      setBusy(true);
      try {
        const { blob, filename } = await api.download(path);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch (e) {
        void message.error(
          e instanceof ApiError && e.status === 429
            ? "ほかの書き出しが進行中です。終わるまで待ってからやり直してください。"
            : errorText(e),
        );
      } finally {
        setBusy(false);
      }
    },
    [api],
  );
  return { run, busy };
}

export function HistoryPage() {
  const navigate = useNavigate();
  const locations = useMyLocations();
  const csv = useCsvDownload();

  const [range, setRange] = useState<[Dayjs, Dayjs] | null>([dayjs().subtract(30, "day").startOf("day"), dayjs().startOf("day")]);
  const [locationId, setLocationId] = useState<number | null>(null);
  const [page, setPage] = useState(1);

  /** 絞り込み(一覧と CSV で共通。ページングは一覧だけ) */
  const filter = useMemo(() => {
    const p = new URLSearchParams();
    if (range) {
      p.set("date_from", range[0].format("YYYY-MM-DD"));
      p.set("date_to", range[1].format("YYYY-MM-DD"));
    }
    if (locationId !== null) p.set("location_id", String(locationId));
    return p;
  }, [range, locationId]);
  const params = useMemo(() => {
    const p = new URLSearchParams(filter);
    p.set("limit", String(PAGE));
    p.set("offset", String((page - 1) * PAGE));
    return p;
  }, [filter, page]);
  const query = useHistory(params);
  const pii = query.data?.pii_columns ?? false;

  const columns: DataColumn<HistoryRow>[] = [
    { title: "日時", ellipsis: false, width: 320, render: (_: unknown, r) => fmtRange(r.starts_at, r.ends_at) },
    { title: "拠点", ellipsis: false, width: 160, render: (_: unknown, r) => r.location_name || `#${r.location_id}` },
    ...(pii
      ? ([
          {
            title: "氏名",
            ellipsis: false,
            width: 180,
            render: (_: unknown, r) =>
              r.anonymized ? (
                <Text type="secondary">(消去済み)</Text>
              ) : r.pii_hidden || !r.name ? (
                <Text type="secondary">—</Text>
              ) : (
                <Space direction="vertical" size={0}>
                  <span>{r.name}</span>
                  {r.name_kana && <Text type="secondary">{r.name_kana}</Text>}
                </Space>
              ),
          },
          { title: "会社", ellipsis: false, width: 160, render: (_: unknown, r) => r.company || <Text type="secondary">—</Text> },
          { title: "件名", ellipsis: true, onCell: (row) => ({ title: row.title || "—" }), width: 320, render: (_: unknown, r) => r.title || <Text type="secondary">—</Text> },
        ] as DataColumn<HistoryRow>[])
      : []),
    { title: "担当者", ellipsis: false, width: 160, render: (_: unknown, r) => r.host_name ?? "(不明)" },
    { title: "種類", ellipsis: false, width: 160, render: (_: unknown, r) => (r.kind === "walkin" ? "飛び込み" : "手動登録") },
    {
      title: "受付",
      ellipsis: false,
      width: 280,
      render: (_: unknown, r) =>
        r.checked_in_at ? (
          <Space direction="vertical" size={0}>
            <span>
              {dayjs(r.checked_in_at).format("MM-DD HH:mm")}
              {r.checkin_route && <Text type="secondary">({CHECKIN_ROUTE[r.checkin_route] ?? r.checkin_route})</Text>}
            </span>
            {r.assurance && (
              <StatusTag tone={r.assurance === "verified" ? "success" : "warning"}>
                {ASSURANCE[r.assurance] ?? r.assurance}
              </StatusTag>
            )}
            {r.verified_method && (
              <Text type="secondary">{VERIFIED_METHOD[r.verified_method] ?? r.verified_method}</Text>
            )}
          </Space>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "来訪者の状態",
      ellipsis: false,
      width: 120,
      render: (_: unknown, r) => (
        <Space direction="vertical" size={0}>
          <span>{VISITOR_STATUS[r.visitor_status] ?? r.visitor_status}</span>
          {r.anonymized && <StatusTag tone="neutral">匿名化済み</StatusTag>}
        </Space>
      ),
    },
    {
      title: "来訪の状態",
      ellipsis: false,
      width: 110,
      render: (_: unknown, r) => {
        const s = VISIT_STATUS[r.visit_status];
        return <StatusTag tone={s?.tone ?? "neutral"}>{s?.label ?? r.visit_status}</StatusTag>;
      },
    },
  ];

  const locOptions = (locations.data?.items ?? []).map((l) => ({ value: l.id, label: l.name }));

  return (
    <div className="ui-list">
      <div className="ui-list-main">
        <PageHeader
          title="履歴"
          description="来訪者ごとの来訪の記録。見える範囲は権限で決まる。保有期間を過ぎた来訪者は個人情報が消えて残る。"
          secondary={
            <Button
              icon={<DownloadOutlined />}
              loading={csv.busy}
              onClick={() => void csv.run(`${CORE}/history.csv?${filter.toString()}`)}
            >
              CSV
            </Button>
          }
        />
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="CSV は基盤の外に出るので、保有期間が来ても消せません。必要な分だけ書き出し、扱いに注意してください。"
        />
        <Space wrap style={{ marginBottom: 12 }}>
          <DatePicker.RangePicker
            value={range}
            onChange={(v) => {
              setRange(v && v[0] && v[1] ? [v[0], v[1]] : null);
              setPage(1);
            }}
            allowClear
          />
          <Select
            allowClear
            style={{ width: 180 }}
            placeholder="拠点"
            options={locOptions}
            value={locationId ?? undefined}
            onChange={(v) => {
              setLocationId(v ?? null);
              setPage(1);
            }}
          />
        </Space>
        {query.isError && <Alert type="error" showIcon style={{ marginBottom: 12 }} message={errorText(query.error)} />}
        <DataTable
          columns={columns}
          rows={query.data?.items ?? []}
          rowKey={(r) => `${r.visit_id}-${r.visitor_id}`}
          loading={query.isLoading}
          total={query.data?.total ?? 0}
          page={page}
          pageSize={PAGE}
          unit="人"
          onPage={(p) => setPage(p)}
          // 匿名化済みの来訪は詳細が 404(サーバーの仕様)なので飛ばさない
          rowClickable={(r) => !r.anonymized}
          onRowClick={(r) => {
            if (!r.anonymized) navigate(`/reception/visits/${r.visit_id}`);
          }}
          empty={{ text: "この条件の来訪はありません" }}
          error={query.isError ? "履歴を読み込めませんでした" : undefined}
        />
      </div>
    </div>
  );
}
